import { Modal } from "../../ui/modal";
import { Wallet } from "../types";
import { DollarSign, ArrowDownToLine, History, ArrowBigRight, ArrowUpToLine } from "lucide-react";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  wallet: Wallet | null;
  onFundWallet: () => void;
  onWithdraw: () => void;
  onTransfer: () => void;
  onViewTransactions: () => void;
}

const WalletActionsModal = ({
  isOpen,
  onClose,
  wallet,
  onFundWallet,
  onWithdraw,
  onViewTransactions,
  onTransfer,
}: Props) => {
  if (!wallet) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      className="max-w-md rounded-2xl border border-gray-200 bg-white p-6
        dark:border-gray-800 dark:bg-gray-900"
    >
      {/* Header */}
      <div className="mb-6">
        <div className="mb-2 flex items-center gap-2">
          {wallet.flag && (
            <img
              src={wallet.flag}
              alt={wallet.currency}
              className="h-6 w-6 rounded-full"
            />
          )}
          <h2 className="text-lg font-semibold text-gray-800 dark:text-white/90">
            {wallet.name} Wallet
          </h2>
        </div>
        
        <div className="text-2xl font-bold text-gray-900 dark:text-white">
          {wallet.symbol} {wallet.balance.toLocaleString()}
        </div>
        
        <div className="text-sm text-gray-500 dark:text-gray-400">
          Available Balance
        </div>
        
        <div className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Held balance: {wallet.symbol} {wallet.heldBalance.toLocaleString()}
        </div>
      </div>

      {/* Status */}
      <div className="mb-6">
        <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium ${
          wallet.status === "ACTIVE"
            ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
            : "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400"
        }`}>
          {wallet.status}
        </span>
      </div>

      {/* Action Cards */}
      <div className="grid gap-4">
        {/* Transfer */}
        <button
          onClick={onTransfer}
          className="flex items-center gap-4 rounded-xl border border-gray-200 bg-white p-4
            text-left transition-all hover:shadow-md hover:border-primary/20
            dark:border-gray-800 dark:bg-gray-800 dark:hover:border-primary/30"
        >
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-orange-100 dark:bg-orange-900/30">
            <ArrowUpToLine className="h-6 w-6 text-orange-600 dark:text-orange-400" />
          </div>
          <div className="flex-1">
            <h3 className="font-medium text-gray-800 dark:text-white/90">Transfer</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Transfer {wallet.currency} to another Monirate wallet
            </p>
          </div>
        </button>
        <button
          onClick={onFundWallet}
          className="flex items-center gap-4 rounded-xl border border-gray-200 bg-white p-4
            text-left transition-all hover:shadow-md hover:border-primary/20
            dark:border-gray-800 dark:bg-gray-800 dark:hover:border-primary/30"
        >
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
            <DollarSign className="h-6 w-6 text-green-600 dark:text-green-400" />
          </div>
          <div className="flex-1">
            <h3 className="font-medium text-gray-800 dark:text-white/90">Fund Wallet</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Add money to your {wallet.currency} wallet
            </p>
          </div>
        </button>

        {/* Withdraw */}
        <button
          onClick={onWithdraw}
          className="flex items-center gap-4 rounded-xl border border-gray-200 bg-white p-4
            text-left transition-all hover:shadow-md hover:border-primary/20
            dark:border-gray-800 dark:bg-gray-800 dark:hover:border-primary/30"
        >
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/30">
            <ArrowDownToLine className="h-6 w-6 text-blue-600 dark:text-blue-400" />
          </div>
          <div className="flex-1">
            <h3 className="font-medium text-gray-800 dark:text-white/90">Withdraw</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Withdraw money from your {wallet.currency} wallet
            </p>
          </div>
        </button>

        {/* View Transactions */}
        <button
          onClick={onViewTransactions}
          className="flex items-center gap-4 rounded-xl border border-gray-200 bg-white p-4
            text-left transition-all hover:shadow-md hover:border-primary/20
            dark:border-gray-800 dark:bg-gray-800 dark:hover:border-primary/30"
        >
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-purple-100 dark:bg-purple-900/30">
            <History className="h-6 w-6 text-purple-600 dark:text-purple-400" />
          </div>
          <div className="flex-1">
            <h3 className="font-medium text-gray-800 dark:text-white/90">View Transactions</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              See all transactions for this wallet
            </p>
          </div>
        </button>
      </div>

      {/* Close Button */}
      <div className="mt-8 flex justify-center">
        <button
          onClick={onClose}
          className="rounded-lg border border-gray-300 bg-white px-6 py-2
            text-sm text-gray-700 hover:bg-gray-100
            dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:hover:bg-gray-700"
        >
          Close
        </button>
      </div>
    </Modal>
  );
};

export default WalletActionsModal;